import React, { useState } from 'react';
import axios from 'axios';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Printer,
  Star,
  ExternalLink,
} from 'lucide-react';
import { siteConfig, googleReviews } from '../data/content';
import BookingModal from '../components/common/BookingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        subject: formData.subject || null,
        message: formData.message,
        source_page: 'contact',
      });

      console.log('Contact form submitted:', response.data);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Contact submission error:', error);
      setSubmitError(
        error.response?.data?.detail || 
        'Failed to send your message. Please try again or call us directly.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    });
    setIsSubmitted(false);
    setSubmitError(null);
    setErrors({});
  };

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 pb-16" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge badge-teal mb-4">Contact Us</span>
            <h1 className="heading-hero mb-6">Get in Touch</h1>
            <p className="body-large">
              Have questions about our services or ready to schedule an appointment? 
              We're here to help.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
              {/* Contact Info */}
              <div className="lg:col-span-2">
                <h2 className="heading-2 mb-6">Contact Information</h2>
                
                <div className="space-y-6 mb-8">
                  {/* Office Phone */}
                  <a
                    href={`tel:${siteConfig.phone}`}
                    className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors group"
                  >
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                      <Phone size={20} className="text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Office Phone</p>
                      <p className="font-mono text-teal-600">{siteConfig.phone}</p>
                      <p className="text-sm text-gray-500 mt-1">Click to call</p>
                    </div>
                  </a>

                  {/* Secondary Phone */}
                  {siteConfig.phoneSecondary && (
                    <a
                      href={`tel:${siteConfig.phoneSecondary}`}
                      className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors group"
                    >
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                        <Phone size={20} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Mobile</p>
                        <p className="font-mono text-teal-600">{siteConfig.phoneSecondary}</p>
                        <p className="text-sm text-gray-500 mt-1">Click to call</p>
                      </div>
                    </a>
                  )}

                  {/* Email */}
                  <a
                    href={`mailto:${siteConfig.email}`}
                    className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors group"
                  >
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                      <Mail size={20} className="text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Email</p>
                      <p className="text-teal-600">{siteConfig.email}</p>
                      <p className="text-sm text-gray-500 mt-1">We'll respond within 24 hours</p>
                    </div>
                  </a>

                  {/* Secure Fax */}
                  {siteConfig.fax && siteConfig.faxVerified && (
                    <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Printer size={20} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 mb-1">Secure Fax</p>
                        <p className="font-mono text-gray-600">{siteConfig.fax}</p>
                        <p className="text-sm text-gray-500 mt-1">For healthcare providers</p>
                      </div>
                    </div>
                  )}

                  {/* Service Area */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin size={20} className="text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Service Area</p>
                      <p className="text-gray-600">{siteConfig.address}</p>
                      <p className="text-gray-600">{siteConfig.city}, {siteConfig.state}</p>
                      <p className="text-sm text-teal-600 mt-2">{siteConfig.serviceArea}</p>
                      {siteConfig.mobileServices && (
                        <p className="text-sm text-gray-500 mt-1">{siteConfig.mobileNote}</p>
                      )}
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock size={20} className="text-teal-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Office Hours</p>
                      <p className="text-gray-600">{siteConfig.hours}</p>
                      {siteConfig.hoursNote && (
                        <p className="text-sm text-gray-500 mt-1">{siteConfig.hoursNote}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Book CTA */}
                <div className="bg-teal-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Ready to Schedule?</h3>
                  <p className="body-small text-gray-600 mb-4">
                    Book a consultation online and we'll confirm your appointment within 1 business day.
                  </p>
                  <button
                    onClick={() => setIsBookingOpen(true)}
                    className="btn-primary w-full"
                  >
                    Book a Consultation
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-3">
                <div className="bg-gray-50 rounded-2xl p-8">
                  <h2 className="heading-2 mb-2">Send Us a Message</h2>
                  <p className="body-small text-gray-600 mb-6">
                    Fill out the form below and we'll get back to you as soon as possible.
                  </p>

                  {/* Error Message */}
                  {submitError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6 flex items-start gap-2">
                      <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{submitError}</p>
                    </div>
                  )}

                  {/* HIPAA Notice */}
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-6 flex items-start gap-2">
                    <AlertCircle size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800">
                      <strong>Privacy Notice:</strong> This form is for general inquiries only. 
                      Please do not include sensitive medical information. Your information is kept confidential.
                    </p>
                  </div>

                  {isSubmitted ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="text-green-600" size={32} />
                      </div>
                      <h3 className="heading-3 mb-2">Message Sent!</h3>
                      <p className="body-medium text-gray-600 mb-6">
                        Thank you for reaching out. We'll respond within 24 hours.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button onClick={resetForm} className="btn-secondary">
                          Send Another Message
                        </button>
                      </div>
                      
                      {/* Google Review CTA after successful submission */}
                      {googleReviews.enabled && googleReviews.reviewsUrl && (
                        <div className="mt-8 pt-6 border-t border-gray-200">
                          <p className="text-sm text-gray-500 mb-3">{googleReviews.ctaAfterSubmit}</p>
                          <a 
                            href={googleReviews.reviewsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm"
                            data-testid="contact-success-google-review"
                          >
                            <Star size={16} className="fill-current" />
                            {googleReviews.ctaLeave}
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="form-label">Full Name *</label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`form-input ${errors.name ? 'border-red-500' : ''}`}
                            placeholder="Your name"
                          />
                          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>
                        <div>
                          <label className="form-label">Email *</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`form-input ${errors.email ? 'border-red-500' : ''}`}
                            placeholder="you@email.com"
                          />
                          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="form-label">Phone (Optional)</label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="form-input"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                        <div>
                          <label className="form-label">Subject</label>
                          <select
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            className="form-input"
                          >
                            <option value="">Select a topic</option>
                            <option value="general">General Inquiry</option>
                            <option value="appointment">Appointment Question</option>
                            <option value="services">Services Information</option>
                            <option value="insurance">Insurance & Billing</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="form-label">Message *</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          className={`form-input form-textarea ${errors.message ? 'border-red-500' : ''}`}
                          placeholder="How can we help you?"
                          rows={5}
                        />
                        {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          'Sending...'
                        ) : (
                          <>
                            Send Message
                            <Send size={16} />
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} sourcePage="contact" />
    </>
  );
};

export default ContactPage;
